import { ComponentTypes } from "../../../src/taskpane/config/config";
import type { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import type { ISharepointListLocation } from "../../Services/LocationService";
import type { IClientGroupSearchSite } from "../../../src/taskpane/components/helpers/sharepointSearch/clientGroupSearchScope";
import { getLastSplittedStringItem } from "../../../src/taskpane/components/helpers/stringHelper";

/** A client-group site as the client-group lookup returns it (hub identity included). */
type IGroupSiteWithHub = IClientGroupSearchSite & { isHub?: boolean; hubId?: string; parentHubId?: string };

/**
 * The directories that open a CLIENT-GROUP site's library from the All documents view: the
 * group site first (exactly as the grid's Client group section opens it, hub identity
 * included), then the library as that site's. Null for the site's own libraries - a library
 * without a site of its own, or of the current site - which keep the usual path.
 */
export const buildGroupLibraryDirectories = (
    library: ISharepointListLocation,
    groupSites: IClientGroupSearchSite[],
    currentSiteId: string | undefined
): IPageSettings[] | null => {
    const siteId: string | undefined = library?.siteId?.trim();
    if (!siteId || !library.siteUrl || siteId.toLowerCase() === currentSiteId?.trim().toLowerCase()) {
        return null;
    }

    const site = (groupSites ?? []).find(candidate => candidate?.id?.trim().toLowerCase() === siteId.toLowerCase()) as IGroupSiteWithHub | undefined;
    const siteDisplayName: string | undefined = library.siteDisplayName ?? site?.displayName;

    const siteDirectory: IPageSettings = {
        type: ComponentTypes.SharePointDocuments,
        siteId,
        siteDisplayName,
        webUrl: library.siteUrl,
        directory: "",
        DocumentLibrary: "",
        displayName: siteDisplayName,
        ...(site?.isHub ? { isHub: true, hubId: site.hubId, parentHubId: site.parentHubId } : {}),
    };

    // Site-relative, compared without case: the site url can come from a cached site in
    // another case than the library's own url.
    const libraryUrl: string = library.webUrl ?? "";
    const directory: string = libraryUrl.toLowerCase().startsWith(`${library.siteUrl.toLowerCase()}/`)
        ? libraryUrl.substring(library.siteUrl.length)
        : getLastSplittedStringItem(libraryUrl, /[\s/]+/);

    const libraryDirectory: IPageSettings = {
        id: library.id,
        type: ComponentTypes.SharePointFolders,
        siteId,
        siteDisplayName,
        webUrl: library.siteUrl,
        fileTitle: library.displayName,
        directory,
        DocumentLibrary: library.displayName,
        displayName: library.displayName,
        // Opened as SharePoint, like a group site's search hits (a Teams breadcrumb would need
        // the team's channels, which this view never read).
        isTeamsFolder: false,
        sharePointListId: library.sharePointListId,
    };

    return [siteDirectory, libraryDirectory];
};
